import React, { useState, useEffect, useRef } from 'react';

function AddressAutocomplete({ value, onChange, onBlur, placeholder = "Введите адрес доставки", required = false }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const justSelectedRef = useRef(false); // Флаг для предотвращения повторного запроса после выбора
  const isUserTypingRef = useRef(false); // Флаг: запрос инициирован пользователем, а не из пропсов

  // Закрываем подсказки при клике вне компонента
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Обнаружение браузерного autofill через нативный change event
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handleNativeChange = () => {
      const nativeValue = el.value;
      if (nativeValue && nativeValue !== inputValueRef.current) {
        // Не ставим isUserTypingRef — не нужно показывать выпадашку при autofill
        setInputValue(nativeValue);
        onChangeRef.current(nativeValue, null);
      }
    };
    el.addEventListener('change', handleNativeChange);
    return () => el.removeEventListener('change', handleNativeChange);
  }, []);

  // Обновляем внутреннее значение при изменении props
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Debounce для запросов к API
  useEffect(() => {
    // Если только что выбрали адрес - пропускаем запрос
    if (justSelectedRef.current) {
      return;
    }

    // Не запускаем автокомплит, если значение пришло из пропсов (localStorage), а не от ввода
    if (!isUserTypingRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (inputValue && inputValue.length >= 3) {
        fetchSuggestions(inputValue);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const fetchSuggestions = async (query) => {
    setIsLoading(true);
    try {
      // Backend ограничивает поиск регионом/радиусом города инстанса (CITY_* в env API)
      const searchQuery = query.trim();

      // Используем правильный API URL (не относительный путь!)
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(
        `${API_URL}/address/suggest?query=${encodeURIComponent(searchQuery)}&count=7`
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setShowSuggestions(true);
      } else {
        console.error('Failed to fetch address suggestions');
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    isUserTypingRef.current = true;
    justSelectedRef.current = false;
    setInputValue(newValue);
    // Передаем null как второй параметр, чтобы сбросить координаты при вводе нового текста
    onChange(newValue, null);
  };

  const handleSuggestionClick = (suggestion) => {
    // Убираем квартиру из адреса (flat) - её пользователь введет отдельно
    let address = suggestion.value;

    // Убираем ", кв X" или ", квартира X" и все что после
    address = address.replace(/,\s*(?:кв\.?|квартира)\s*\d+.*$/i, '');

    // Если есть номер дома - закрываем список и завершаем
    if (suggestion.data && suggestion.data.house) {
      justSelectedRef.current = true;
      isUserTypingRef.current = false; // Сбрасываем — дальнейшие изменения inputValue от props, не от ввода
      setInputValue(address);
      onChange(address, suggestion.data);
      setShowSuggestions(false);
      setSuggestions([]);
      // Скрываем клавиатуру
      if (inputRef.current) inputRef.current.blur();
    } else {
      // Если нет номера дома (выбрана только улица) - добавляем ", " в конец
      // Это запустит новый поиск домов на этой улице
      const addressWithComma = address + ', ';
      isUserTypingRef.current = true;
      setInputValue(addressWithComma);
      onChange(addressWithComma);

      // Устанавливаем фокус и курсор в конец строки
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = addressWithComma.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 50);

      // Список обновится автоматически через useEffect с debounce
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        className="form-input"
        autoComplete="street-address"
        style={{
          width: '100%',
          padding: '12px',
          paddingRight: inputValue ? '36px' : '12px',
          fontSize: '14px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          outline: 'none',
          transition: 'border-color 0.2s'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--primary-color)';
          handleInputFocus();
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#e0e0e0';
          // Если только что выбрали адрес из списка — не перезатираем данные
          if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
          }
          // Проверяем, не подставил ли браузер значение через autofill
          const currentValue = e.target.value;
          if (currentValue !== inputValue) {
            isUserTypingRef.current = true;
            setInputValue(currentValue);
            onChange(currentValue, null);
          }
          if (onBlur) onBlur(currentValue);
        }}
      />

      {inputValue && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setInputValue('');
            onChange('', null);
            setSuggestions([]);
            setShowSuggestions(false);
            if (inputRef.current) inputRef.current.focus();
          }}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            fontSize: '16px',
            lineHeight: 1,
            color: '#999',
            zIndex: 2
          }}
        >
          ✕
        </button>
      )}

      {isLoading && (
        <div style={{
          position: 'absolute',
          right: inputValue ? '36px' : '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '12px',
          color: '#999'
        }}>
          Поиск...
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {suggestions.map((suggestion, index) => {
            // Убираем "г <город инстанса>, " из начала для более чистого отображения
            const displayValue = suggestion.value.replace(new RegExp(`^г ${process.env.REACT_APP_BRAND_CITY || ''},?\\s*`, 'i'), '');

            return (
              <div
                key={index}
                onMouseDown={(e) => {
                  e.preventDefault(); // Предотвращаем потерю фокуса с input
                  handleSuggestionClick(suggestion);
                }}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  fontSize: '14px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9f9f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ fontWeight: 500 }}>
                  {displayValue}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showSuggestions && !isLoading && inputValue.length >= 3 && suggestions.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          padding: '12px',
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: '14px',
          color: '#999',
          textAlign: 'center',
          zIndex: 1000
        }}>
          Адрес не найден
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
